import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";
import { strToU8, zipSync } from "fflate";
import {
  type BusinessMetricDictionaryReadModel,
  BusinessMetricDictionaryReadModelSchema,
  type BusinessMetricReadModel,
  isBusinessMetricDictionaryReadModel,
} from "./business-metrics.js";

export const BUSINESS_METRIC_REPORT_FORMAT_VERSION = "BUSINESS_METRIC_REPORT_CSV_V1" as const;
export const BUSINESS_METRIC_REPORT_BUNDLE_FORMAT_VERSION =
  "BUSINESS_METRIC_REPORT_BUNDLE_V2" as const;

const availabilityLabels: Record<BusinessMetricReadModel["availability"], string> = {
  AVAILABLE: "数据完整",
  PARTIAL: "部分可用",
  NOT_AVAILABLE: "暂不可用",
};

const coverageLabels: Record<BusinessMetricDictionaryReadModel["coverage"], string> = {
  CURRENT_BUSINESS_DAY: "当前营业日",
  RECORDED_FACTS: "历史记录事实",
  MISSING_HISTORY: "历史资料缺失",
};

const drillLabels: Record<BusinessMetricReadModel["drillTarget"], string> = {
  ORDERS: "订单明细",
  RECONCILIATION: "收款与退款明细",
  MEMBER_FUNDS: "会员资金明细",
  RECEIVABLES: "挂账应收明细",
  COACH_SERVICES: "助教服务明细",
  INVENTORY: "库存明细",
  CASH_SHIFTS: "现金班次明细",
};

const CSV_HEADERS = [
  "口径版本",
  "资料范围",
  "营业日",
  "生成时间",
  "指标",
  "说明",
  "结果",
  "单位",
  "可用状态",
  "事实条数",
  "事实来源",
  "原因",
  "明细入口",
] as const;

type BusinessMetricReportCell = string | number | null;

function businessMetricReportRows(
  dictionary: BusinessMetricDictionaryReadModel,
): BusinessMetricReportCell[][] {
  return dictionary.metrics.map((metric) => [
    "经营指标口径 v1",
    coverageLabels[dictionary.coverage],
    dictionary.businessDate,
    dictionary.generatedAt,
    metric.label,
    metric.explanation,
    metric.value,
    metric.unit === "CNY_FEN" ? "分" : "项",
    availabilityLabels[metric.availability],
    metric.factCount,
    metric.sourceLabel,
    metric.reason ?? "",
    drillLabels[metric.drillTarget],
  ]);
}

function csvText(value: string): string {
  const neutralized = /^[=+\-@\t\r]/u.test(value) ? `'${value}` : value;
  return `"${neutralized.replaceAll('"', '""')}"`;
}

function csvNumber(value: number | null): string {
  return value === null ? '""' : `"${value}"`;
}

export function businessMetricReportFileName(businessDate: string): string {
  return `经营指标-${businessDate}.csv`;
}

export function businessMetricReportXlsxFileName(businessDate: string): string {
  return `经营指标-${businessDate}.xlsx`;
}

export function buildBusinessMetricReportCsv(
  dictionary: BusinessMetricDictionaryReadModel,
): string {
  const rows = businessMetricReportRows(dictionary).map((row) =>
    row
      .map((cell) => (typeof cell === "number" || cell === null ? csvNumber(cell) : csvText(cell)))
      .join(","),
  );
  return `\uFEFF${CSV_HEADERS.map(csvText).join(",")}\r\n${rows.join("\r\n")}\r\n`;
}

function xmlText(value: string): string {
  const xmlSafe = Array.from(value, (character) => {
    const codePoint = character.codePointAt(0) as number;
    const allowed =
      codePoint === 0x09 ||
      codePoint === 0x0a ||
      codePoint === 0x0d ||
      (codePoint >= 0x20 && codePoint <= 0xd7ff) ||
      (codePoint >= 0xe000 && codePoint <= 0xfffd) ||
      (codePoint >= 0x10000 && codePoint <= 0x10ffff);
    return allowed ? character : "\uFFFD";
  }).join("");
  return xmlSafe
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&apos;");
}

function xlsxColumnName(index: number): string {
  let value = index + 1;
  let result = "";
  while (value > 0) {
    value -= 1;
    result = String.fromCharCode(65 + (value % 26)) + result;
    value = Math.floor(value / 26);
  }
  return result;
}

function xlsxCell(cell: BusinessMetricReportCell, rowIndex: number, columnIndex: number): string {
  const reference = `${xlsxColumnName(columnIndex)}${rowIndex}`;
  if (cell === null) return `<c r="${reference}"/>`;
  if (typeof cell === "number") return `<c r="${reference}" s="2"><v>${cell}</v></c>`;
  return `<c r="${reference}" t="inlineStr"><is><t xml:space="preserve">${xmlText(cell)}</t></is></c>`;
}

function xlsxWorksheet(dictionary: BusinessMetricDictionaryReadModel): string {
  const rows: BusinessMetricReportCell[][] = [
    [...CSV_HEADERS],
    ...businessMetricReportRows(dictionary),
  ];
  const sheetRows = rows
    .map((row, rowOffset) => {
      const rowIndex = rowOffset + 1;
      const cells = row
        .map((cell, columnIndex) => {
          const value = xlsxCell(cell, rowIndex, columnIndex);
          return rowIndex === 1 ? value.replace("<c ", '<c s="1" ') : value;
        })
        .join("");
      return `<row r="${rowIndex}">${cells}</row>`;
    })
    .join("");
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><dimension ref="A1:M${rows.length}"/><sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews><sheetFormatPr defaultRowHeight="15"/><cols><col min="1" max="4" width="20" customWidth="1"/><col min="5" max="5" width="18" customWidth="1"/><col min="6" max="6" width="56" customWidth="1"/><col min="7" max="10" width="14" customWidth="1"/><col min="11" max="13" width="28" customWidth="1"/></cols><sheetData>${sheetRows}</sheetData><autoFilter ref="A1:M${rows.length}"/></worksheet>`;
}

export function buildBusinessMetricReportXlsx(
  dictionary: BusinessMetricDictionaryReadModel,
): Uint8Array {
  const files = {
    "[Content_Types].xml": strToU8(
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/docProps/app.xml" ContentType="application/vnd.openxmlformats-officedocument.extended-properties+xml"/><Override PartName="/docProps/core.xml" ContentType="application/vnd.openxmlformats-package.core-properties+xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/><Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/></Types>',
    ),
    "_rels/.rels": strToU8(
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/><Relationship Id="rId2" Type="http://schemas.openxmlformats.org/package/2006/relationships/metadata/core-properties" Target="docProps/core.xml"/><Relationship Id="rId3" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/extended-properties" Target="docProps/app.xml"/></Relationships>',
    ),
    "docProps/app.xml": strToU8(
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n<Properties xmlns="http://schemas.openxmlformats.org/officeDocument/2006/extended-properties" xmlns:vt="http://schemas.openxmlformats.org/officeDocument/2006/docPropsVTypes"><Application>Billiard OS</Application><DocSecurity>0</DocSecurity><ScaleCrop>false</ScaleCrop><HeadingPairs><vt:vector size="2" baseType="variant"><vt:variant><vt:lpstr>工作表</vt:lpstr></vt:variant><vt:variant><vt:i4>1</vt:i4></vt:variant></vt:vector></HeadingPairs><TitlesOfParts><vt:vector size="1" baseType="lpstr"><vt:lpstr>经营指标</vt:lpstr></vt:vector></TitlesOfParts><Company></Company><LinksUpToDate>false</LinksUpToDate><SharedDoc>false</SharedDoc><HyperlinksChanged>false</HyperlinksChanged><AppVersion>1.0</AppVersion></Properties>',
    ),
    "docProps/core.xml": strToU8(
      `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n<cp:coreProperties xmlns:cp="http://schemas.openxmlformats.org/package/2006/metadata/core-properties" xmlns:dc="http://purl.org/dc/elements/1.1/" xmlns:dcterms="http://purl.org/dc/terms/" xmlns:dcmitype="http://purl.org/dc/dcmitype/" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"><dc:title>经营指标 ${xmlText(dictionary.businessDate)}</dc:title><dc:creator>Billiard OS</dc:creator><cp:lastModifiedBy>Billiard OS</cp:lastModifiedBy><dcterms:created xsi:type="dcterms:W3CDTF">${xmlText(dictionary.generatedAt)}</dcterms:created><dcterms:modified xsi:type="dcterms:W3CDTF">${xmlText(dictionary.generatedAt)}</dcterms:modified></cp:coreProperties>`,
    ),
    "xl/workbook.xml": strToU8(
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets><sheet name="经营指标" sheetId="1" r:id="rId1"/></sheets><calcPr calcId="0" fullCalcOnLoad="1"/></workbook>',
    ),
    "xl/_rels/workbook.xml.rels": strToU8(
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/><Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/></Relationships>',
    ),
    "xl/styles.xml": strToU8(
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><fonts count="2"><font><sz val="11"/><name val="Microsoft YaHei"/><family val="2"/></font><font><b/><sz val="11"/><color rgb="FFFFFFFF"/><name val="Microsoft YaHei"/><family val="2"/></font></fonts><fills count="3"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill><fill><patternFill patternType="solid"><fgColor rgb="FF1F4E78"/><bgColor indexed="64"/></patternFill></fill></fills><borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders><cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs><cellXfs count="3"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/><xf numFmtId="0" fontId="1" fillId="2" borderId="0" xfId="0" applyFont="1" applyFill="1"/><xf numFmtId="1" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/></cellXfs><cellStyles count="1"><cellStyle name="常规" xfId="0" builtinId="0"/></cellStyles></styleSheet>',
    ),
    "xl/worksheets/sheet1.xml": strToU8(xlsxWorksheet(dictionary)),
  };
  return zipSync(files, { level: 6, mtime: new Date("1980-01-01T00:00:00.000Z") });
}

const BASE64_ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/" as const;

export function businessMetricReportBytesToBase64(bytes: Uint8Array): string {
  let result = "";
  for (let index = 0; index < bytes.length; index += 3) {
    const first = bytes[index] ?? 0;
    const second = bytes[index + 1] ?? 0;
    const third = bytes[index + 2] ?? 0;
    const combined = (first << 16) | (second << 8) | third;
    result += BASE64_ALPHABET[(combined >> 18) & 63];
    result += BASE64_ALPHABET[(combined >> 12) & 63];
    result += index + 1 < bytes.length ? BASE64_ALPHABET[(combined >> 6) & 63] : "=";
    result += index + 2 < bytes.length ? BASE64_ALPHABET[combined & 63] : "=";
  }
  return result;
}

export const BusinessMetricReportReadModelSchema = Type.Object(
  {
    formatVersion: Type.Literal(BUSINESS_METRIC_REPORT_FORMAT_VERSION),
    tenantId: Type.String({ minLength: 1, maxLength: 128 }),
    storeId: Type.String({ minLength: 1, maxLength: 128 }),
    businessDate: Type.String({ pattern: "^\\d{4}-\\d{2}-\\d{2}$" }),
    generatedAt: Type.String({ minLength: 1, maxLength: 64 }),
    fileName: Type.String({ minLength: 1, maxLength: 64 }),
    csv: Type.String({ minLength: 1, maxLength: 65_536 }),
    contentSha256: Type.String({ pattern: "^[a-f0-9]{64}$" }),
    dictionary: BusinessMetricDictionaryReadModelSchema,
  },
  { additionalProperties: false },
);

export type BusinessMetricReportReadModel = Static<typeof BusinessMetricReportReadModelSchema>;

export const BusinessMetricReportBundleReadModelSchema = Type.Object(
  {
    formatVersion: Type.Literal(BUSINESS_METRIC_REPORT_BUNDLE_FORMAT_VERSION),
    tenantId: Type.String({ minLength: 1, maxLength: 128 }),
    storeId: Type.String({ minLength: 1, maxLength: 128 }),
    businessDate: Type.String({ pattern: "^\\d{4}-\\d{2}-\\d{2}$" }),
    generatedAt: Type.String({ minLength: 1, maxLength: 64 }),
    csvFileName: Type.String({ minLength: 1, maxLength: 64 }),
    csv: Type.String({ minLength: 1, maxLength: 65_536 }),
    csvSha256: Type.String({ pattern: "^[a-f0-9]{64}$" }),
    xlsxFileName: Type.String({ minLength: 1, maxLength: 64 }),
    xlsxBase64: Type.String({
      minLength: 1,
      maxLength: 524_288,
      pattern: "^[A-Za-z0-9+/]+={0,2}$",
    }),
    xlsxSha256: Type.String({ pattern: "^[a-f0-9]{64}$" }),
    dictionary: BusinessMetricDictionaryReadModelSchema,
  },
  { additionalProperties: false },
);

export type BusinessMetricReportBundleReadModel = Static<
  typeof BusinessMetricReportBundleReadModelSchema
>;

export function isBusinessMetricReportReadModel(
  value: unknown,
  expectedScope?: { tenantId: string; storeId: string; businessDate: string },
): value is BusinessMetricReportReadModel {
  if (!Value.Check(BusinessMetricReportReadModelSchema, value)) return false;
  if (
    !isBusinessMetricDictionaryReadModel(value.dictionary, {
      tenantId: value.tenantId,
      storeId: value.storeId,
      businessDate: value.businessDate,
    }) ||
    value.generatedAt !== value.dictionary.generatedAt ||
    value.fileName !== businessMetricReportFileName(value.businessDate) ||
    value.csv !== buildBusinessMetricReportCsv(value.dictionary)
  ) {
    return false;
  }
  return (
    expectedScope === undefined ||
    (value.tenantId === expectedScope.tenantId &&
      value.storeId === expectedScope.storeId &&
      value.businessDate === expectedScope.businessDate)
  );
}

export function isBusinessMetricReportBundleReadModel(
  value: unknown,
  expectedScope?: { tenantId: string; storeId: string; businessDate: string },
  expectedXlsx?: Uint8Array,
): value is BusinessMetricReportBundleReadModel {
  if (!Value.Check(BusinessMetricReportBundleReadModelSchema, value)) return false;
  if (
    !isBusinessMetricDictionaryReadModel(value.dictionary, {
      tenantId: value.tenantId,
      storeId: value.storeId,
      businessDate: value.businessDate,
    }) ||
    value.generatedAt !== value.dictionary.generatedAt ||
    value.csvFileName !== businessMetricReportFileName(value.businessDate) ||
    value.csv !== buildBusinessMetricReportCsv(value.dictionary) ||
    value.xlsxFileName !== businessMetricReportXlsxFileName(value.businessDate) ||
    value.xlsxBase64 !==
      businessMetricReportBytesToBase64(
        expectedXlsx ?? buildBusinessMetricReportXlsx(value.dictionary),
      )
  ) {
    return false;
  }
  return (
    expectedScope === undefined ||
    (value.tenantId === expectedScope.tenantId &&
      value.storeId === expectedScope.storeId &&
      value.businessDate === expectedScope.businessDate)
  );
}
